import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTablePagination } from './DataTablePagination';
import { TableComponent } from '../Table/TableComponent';
import { GlobalFilter } from '@/components/Common';
import { Spinner } from '@/components/Common';
import { SearchFilter } from '@/store/types';
import { useAwmsStore } from '@/store';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    custom: unknown;
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: Dispatch<SetStateAction<ColumnFiltersState>>;
  hasBandedRows?: boolean;
  hasGlobalFilterHeader?: boolean;
  hasFilterHeader?: boolean;
  customHeaderSlot?: ReactNode;
  dividerColumnProps?: DividerColumnProps;
  tableProps?: TableProps;
  globalFilterHeaderProps?: GlobalFilterHeaderProps;
  filterHeaderProps?: FilterHeaderProps;
  headerProps?: HeaderProps;
  footerProps?: FooterProps;
  id: string;
  isPageLoading?: boolean;
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination?: Dispatch<SetStateAction<{ pageIndex: number; pageSize: number }>>;
  defaultSort?: {
    id: string;
    desc?: boolean;
  };
}

interface TableProps {
  padding?: string;
  disableBottomBorder?: boolean;
  fontSize?: string;
}

export interface Option {
  label: string;
  value: number;
}

export interface FilterOption {
  placeholder: string;
  checkbox: boolean;
  options: Option[];
  stateFilterFieldName: string;
  stateFilterValue?: Option;
  selectedState: number | SearchFilter[];
  setSelectedState: (value: number | SearchFilter[]) => void;
  className?: string;
}

interface GlobalFilterHeaderProps {
  searchColumn: {
    searchPlaceHolder: string;
    columnId: string;
  };
  filterOptions: FilterOption[];
}

interface FilterHeaderProps {
  searchColumn: {
    searchPlaceHolder: string;
    columnId: string;
  };
}

interface HeaderProps {
  disableDefaultBorder?: boolean;
  primaryColor: string;
  secondaryColor?: string;
  borderColor?: string;
}
interface FooterProps {
  disablePagination?: boolean;
  className?: string;
}

interface DividerColumnProps {
  dividerColumnId: string;
  borderStyling: string;
}

export const DataTable = <TData, TValue>({
  columns,
  data,
  columnFilters: sheetColumnFilters, //sheetColumnFilters is for HDR and StaffUnit sheets
  setColumnFilters,
  dividerColumnProps,
  tableProps,
  globalFilterHeaderProps,
  headerProps,
  footerProps,
  customHeaderSlot,
  hasGlobalFilterHeader,
  hasBandedRows,
  id,
  isPageLoading,
  pagination,
  setPagination,
  defaultSort,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>(() =>
    defaultSort ? [{ id: defaultSort.id, desc: defaultSort.desc ?? false }] : [],
  );
  const [columnFiltersState, setColumnFiltersState] = useState<ColumnFiltersState>(sheetColumnFilters || []);
  const columnFilters = useAwmsStore((state) => state.columnFilters);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(!footerProps?.disablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
    }),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFiltersState,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters: columnFiltersState,
      ...(!footerProps?.disablePagination && { pagination }),
    },
    ...(!footerProps?.disablePagination && { onPaginationChange: setPagination }),
    autoResetPageIndex: false,
  });
  let dividerIndex: number;
  table.getHeaderGroups()[0]?.headers.map((header) => {
    if (header.id == dividerColumnProps?.dividerColumnId) dividerIndex = header.index;
  });

  // Effect to update columnFiltersState whenever the passed columnFilters change
  useEffect(() => {
    if (sheetColumnFilters != undefined && sheetColumnFilters !== columnFiltersState) {
      setColumnFiltersState(sheetColumnFilters || []);
    }
  }, [sheetColumnFilters]);

  useEffect(() => {
    // Update the parent (Hdr) whenever column filters change
    if (setColumnFilters) {
      setColumnFilters(columnFiltersState);
    }
  }, [columnFiltersState, setColumnFilters]);

  useEffect(() => {
    const numberOfRows = table.getFilteredRowModel().rows.length;
    if ((pagination?.pageIndex ?? 0) > Math.floor(numberOfRows / (pagination?.pageSize ?? 1))) {
      if (setPagination) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: 0, // reset if out of bounds
        }));
      }
    }
  }, [data, pagination?.pageIndex, pagination?.pageSize, columnFilters]);

  return (
    <>
      <div>
        {hasGlobalFilterHeader && globalFilterHeaderProps?.searchColumn && (
          <GlobalFilter
            column={table.getColumn(globalFilterHeaderProps?.searchColumn?.columnId)}
            placeHolder={globalFilterHeaderProps?.searchColumn?.searchPlaceHolder}
            filterOptions={globalFilterHeaderProps?.filterOptions}
          />
        )}
        <div
          className={`overflow-auto ${tableProps?.disableBottomBorder ? '' : 'border-b-2 border-grey-200'} bg-white
            ${tableProps?.padding ? tableProps.padding : ''}`}
        >
          <TableComponent id={id}>
            <TableHeader className="top-0 z-10 table-header-group bg-white md:sticky">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="!border-b-0">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={`h-0 text-nowrap bg-deep-blue-50 pl-0 ${headerProps?.disableDefaultBorder ? '' : 'border-b'} ${
                        headerProps?.borderColor ? `border-${headerProps.borderColor}` : 'border-deep-blue' } ${
                        header.column.id === dividerColumnProps?.dividerColumnId
                            ? dividerColumnProps.borderStyling
                            : ''
                        }
                        ${dividerIndex && headerProps?.secondaryColor && dividerIndex < header.index ? (headerProps?.secondaryColor ?? headerProps?.primaryColor ?? 'bg-white') : (headerProps?.primaryColor ?? 'bg-white')}`}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
              {customHeaderSlot}
            </TableHeader>
            <TableBody className="overflow-y-auto">
              {table?.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    isHoverable
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`${hasBandedRows ? 'even:bg-secondary-grey-05' : 'bg-white'}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const currentColumnIndex = cell.column.getIndex();
                      return (
                        <TableCell
                          key={cell.id}
                          className={`h-auto p-0 ${
                            cell.column.id === dividerColumnProps?.dividerColumnId
                              ? dividerColumnProps.borderStyling
                              : ''
                            }`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {isPageLoading ? <Spinner /> : 'No results'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {footerProps && table.getRowModel().rows?.length > 0 && (
              <TableFooter>
                {table.getFooterGroups().map((footerGroup) => {
                  return (
                    <TableRow key={footerGroup.id} className={`h-[55px] bg-white ${footerProps.className}`}>
                      {footerGroup.headers.map((footer) => {
                        return (
                          <TableCell
                            key={footer.id}
                            className={`my-auto h-0 p-0 ${
                            footer.column.id === dividerColumnProps?.dividerColumnId
                                ? dividerColumnProps.borderStyling
                                : ''
                            }`}
                          >
                            {footer.isPlaceholder
                              ? null
                              : flexRender(footer.column.columnDef.footer, footer.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableFooter>
            )}
          </TableComponent>
        </div>
      </div>
      {!footerProps?.disablePagination && (
        <div className="sticky bottom-0 z-20 bg-white p-4">
          <DataTablePagination table={table} />
        </div>
      )}
    </>
  );
};
